import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import DeletePageDialogFields from "../../field/page/DeletePageDialogFields";
import DocumentSettings from "../../../document/DocumentSettings";
import PageSelection, {PAGERANGE_ALL} from "../../../document/PageSelection";
import MimeType from "../../../document/mimetype/MimeType";
import PageRange from "../../../../shared/components/PageRange";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	Functions,
	Information,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	ToolboxDeleteDelete
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Info from "../../../../shared/components/Info";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

interface DeletePageDialogStatus extends PortalDialogState<ToolboxDeleteDelete> {
	pages: number;
	currentPage: string;
}

export class DeletePageDialogDefinition extends PortalDialog<ToolboxDeleteDelete,
	PortalDialogProperties<ToolboxDeleteDelete>,
	DeletePageDialogStatus> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			pages: 1,
			currentPage: "1",
			fieldErrors: new Map<DeletePageDialogFields, string>()
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.deletePages, Functions.getDeletePagesDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.DELETE_PAGE, signatureEditProfile, Intention.editingContent, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderContents(): ReactNode {
		let parameter: ToolboxDeleteDelete | undefined = this.state.parameter?.data;
		let errors: Map<DeletePageDialogFields, string> = this.state.fieldErrors;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		return (
			<>
				<fieldset className="p-col-12 p-grid" hidden={
					!this.state.fields?.contains(DeletePageDialogFields.PAGERANGE)
				}>
					<div className="p-col-12 p-grid">
						<label className="p-col-12 p-sm-4" htmlFor="control-pagerange">
							<Translate {...this.props} data="delete_page.pages.label" namespace="dialog"/>
							<Info {...this.props} data="delete_page.pages.info" options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-8">
							<PageRange inputId="control-pagerange"
									   className={errors.has(DeletePageDialogFields.PAGERANGE) ? " p-invalid" : ""}
									   value={FunctionHelper.applyValue(
										   parameter?.pages, ToolboxDeleteDelete.getPagesDefault()
									   )}
									   onChange={(e) => this.onChange(
										   DeletePageDialogFields.PAGERANGE, e.value
									   )}
									   appendTo={document.getElementById("root")}
									   addon={Translate.value(
										   "inputs.pagerange.of", {pages: this.state.pages}
									   )}
									   tooltip={errors.has(DeletePageDialogFields.PAGERANGE) ? Translate.value(
										   errors.get(DeletePageDialogFields.PAGERANGE)!, {ns: "message"}
									   ) : ""}
									   options={[
										   {label: Translate.value("inputs.pagerange.all"), value: PAGERANGE_ALL},
										   {
											   label: Translate.value("inputs.pagerange.current"),
											   value: this.state.currentPage
										   }
									   ]}/>
							{this.renderFieldError(errors.has(DeletePageDialogFields.PAGERANGE) ?
								Translate.value(
									errors.get(DeletePageDialogFields.PAGERANGE)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["delete-page-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.delete_page.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected onChange(field: DeletePageDialogFields, value: any): void {
		let parameter: ToolboxDeleteDelete = this.state.parameter!.data;
		let errors: Map<DeletePageDialogFields, string> = this.state.fieldErrors;

		switch (field) {
			case DeletePageDialogFields.PAGERANGE:
				let checkPagerange: boolean = true;

				try {
					PageSelection.fromString(value, this.state.pages);
				} catch (e: any) {
					checkPagerange = false;
				}

				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!checkPagerange) {
					errors.set(field, "errors.fields.pagerange");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.pages = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-delete-page small"}/>
					<Translate {...this.props} data="delete_page.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canAssemble, Security.getCanAssembleDefault()
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: ToolboxDeleteDelete | undefined = this.state.parameter?.data;
		let portalDocument: PortalDocument = this.props.context.documentManager.getSelection()[0];
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		let currentPage: string = documentSettings.activeViewer.currentPage.toString();
		let pageCount: number = await portalDocument.getNumberOfPages();

		if (!FunctionHelper.exists(parameter)) {
			return;
		}

		parameter!.pages = documentSettings.pageSelection.toString(
			pageCount, documentSettings.activeViewer.currentPage
		);

		this.setState({
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone(),
			pages: pageCount,
			currentPage: currentPage
		});
	};

	protected async submit(): Promise<void> {
		await super.submit();

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			delete: this.state.parameter!.data.toJson()
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.page.delete", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
			this.hide();
		}
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.DELETE_PAGE;
	}

	protected getDefaultParameter(): Parameter<ToolboxDeleteDelete> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const DeletePageDialog = React.memo(DeletePageDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<ToolboxDeleteDelete>>,
		nextProps: Readonly<PortalDialogProperties<ToolboxDeleteDelete>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default DeletePageDialog;